import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';

import { useError } from '@zenbusiness/application-commons-logging';
import PageLoading from '@zenbusiness/zui/PageLoading';

import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { useTaxClassification } from '#client/pages/ProductLanding/hooks/useTaxClassification';

/**
 * Component that checks if the business entity has a tax classification
 * and redirects to the redirect path if the condition is met.
 * @param {object} props
 * @param {Function} [props.shouldRedirect] - A conditional function that returns a truthy value to trigger the redirection.
 * @param {string} [props.redirectPath] - The absolute path to redirect to if the condition is met (will be processed by useDashboardPath).
 * @param {import('react').ReactNode} [props.children] - The children to render.
 * @component
 */
export function TaxClassificationGate({ shouldRedirect = () => false, redirectPath = '/products', children }) {
  const dashboardRedirectPath = useDashboardPath(redirectPath);
  const { taxClassification, loading, error } = useTaxClassification();

  useError(error);
  // If there are errors, redirect as the product cannot be determined or accessed for this business entity
  if (error) {
    return <Navigate to={dashboardRedirectPath} replace />;
  }

  if (loading || !taxClassification) {
    return <PageLoading />;
  }

  if (shouldRedirect(taxClassification)) {
    return <Navigate to={dashboardRedirectPath} replace />;
  }

  return children;
}

TaxClassificationGate.propTypes = {
  shouldRedirect: PropTypes.func,
  redirectPath: PropTypes.string,
  children: PropTypes.node.isRequired
};
